import {
  Drawer,
  Group,
  Stack,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import { Button, CountrySelector, Loader, Select } from "@tfg/ui"
import { sortBy } from "lodash"
import { useNavigate } from "react-router-dom"

import {
  organizationSchema,
  organizationValidationType,
} from "@tfg/types/schemas/admin"
import { OrganizationType } from "@tfg/types/types/organization.types"

import { COUNTRIES } from "~/utils/countries"

import { LoginAs } from "./OrganizationDetails"
import {
  useCreateOrganizationMutation,
  useListOrganizationsQuery,
} from "./hooks"

export const OrganizationsAdmin = () => {
  const {
    data: organizations,
    isLoading,
    isError,
  } = useListOrganizationsQuery({
    type: [OrganizationType.BRAND, OrganizationType.PLATFORM_ADMIN],
  })

  const navigate = useNavigate()

  const [drawerOpen, drawerActions] = useDisclosure(false)

  const { mutate: createOrganization, isPending: isCreating } =
    useCreateOrganizationMutation({
      onSuccess: drawerActions.close,
    })
  const form = useForm<organizationValidationType>({
    validate: zodResolver(organizationSchema),
    initialValues: {
      name: "",
      agecProductPagePlan: "STARTER",
      outputCustomizationPlan: "ESSENTIAL",
      lcaCustomizationPlan: "ADVANCED",
      customFieldsPlan: "ADVANCED",
      certificatesPlan: "ADVANCED",
      zalandoPlan: "DISABLED",
      rduePlan: "DISABLED",
      businessType: "SMALL_BUSINESS",
      type: "BRAND",
      country: "FR",
      city: "",
      street: "",
      postalCode: "",
      registrationId: "",
    },
  })
  const handleSubmit = (values: organizationValidationType) => {
    createOrganization(values)
  }
  if (isError) return <Text>Organizations could not be loaded</Text>

  return (
    <Stack>
      <Group justify="right">
        <Button onClick={drawerActions.open} icon={<IconPlus />}>
          Create Organization
        </Button>
      </Group>
      {isLoading ? (
        <Loader fullPage />
      ) : (
        <Table verticalSpacing="sm" highlightOnHover>
          <TableThead>
            <TableTr>
              <TableTh>Name</TableTh>
              <TableTh />
            </TableTr>
          </TableThead>
          <TableTbody>
            {sortBy(organizations, (organization) => organization.name).map(
              (organization) => (
                <TableTr
                  key={organization.id}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/admin/organizations/${organization.id}`)
                  }
                >
                  <TableTd>{organization.name}</TableTd>
                  <TableTd align="right">
                    {organization.users?.[0] && (
                      <Group gap="var(--tfg-molecule-padding-y)" w="100%">
                        <LoginAs
                          showEmail={true}
                          user={organization.users[0]}
                          withoutAdmin={true}
                          onLogin={() => window.location.replace("/")}
                        />
                      </Group>
                    )}
                  </TableTd>
                </TableTr>
              )
            )}
          </TableTbody>
        </Table>
      )}
      <Drawer
        position="right"
        opened={drawerOpen}
        onClose={() => drawerActions.close()}
        title={<Title order={2}>Create brand</Title>}
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Name"
              placeholder="Company name"
              error={form.errors.name}
              {...form.getInputProps("name")}
            />
            <TextInput
              label="Registration ID"
              placeholder="Registration ID"
              error={form.errors.registrationId}
              {...form.getInputProps("registrationId")}
            />
            <CountrySelector
              value={form.values.country}
              countriesMapper={COUNTRIES}
              label="Country"
              placeholder="Country"
              error={form.errors.country}
              {...form.getInputProps("country")}
            />
            <TextInput
              label="City"
              placeholder="City"
              error={form.errors.city}
              {...form.getInputProps("city")}
            />
            <TextInput
              label="Street"
              placeholder="Street"
              error={form.errors.street}
              {...form.getInputProps("street")}
            />
            <TextInput
              label="Postal Code"
              placeholder="Postal Code"
              error={form.errors.postalCode}
              {...form.getInputProps("postalCode")}
            />
            <Select
              label="Agec Product Page Plan"
              data={[
                { label: "Starter", value: "STARTER" },
                { label: "Essential", value: "ESSENTIAL" },
                { label: "Advanced", value: "ADVANCED" },
              ]}
              error={form.errors.agecProductPagePlan}
              {...form.getInputProps("agecProductPagePlan")}
            />
            <Select
              label="Output Customization Plan"
              data={[
                { label: "Essential", value: "ESSENTIAL" },
                { label: "Advanced", value: "ADVANCED" },
                { label: "Expert", value: "EXPERT" },
              ]}
              error={form.errors.outputCustomizationPlan}
              {...form.getInputProps("outputCustomizationPlan")}
            />
            <Select
              label="LCA"
              data={[
                { label: "Advanced", value: "ADVANCED" },
                { label: "Expert", value: "EXPERT" },
              ]}
              error={form.errors.lcaCustomizationPlan}
              {...form.getInputProps("lcaCustomizationPlan")}
            />
            <Select
              label="Certificates"
              data={[
                { label: "Advanced", value: "ADVANCED" },
                { label: "Expert", value: "EXPERT" },
              ]}
              error={form.errors.certificatesPlan}
              {...form.getInputProps("certificatesPlan")}
            />
            <Select
              label="Rdue Plan"
              data={[
                { label: "Yes", value: "ENABLED" },
                { label: "No", value: "DISABLED" },
              ]}
              error={form.errors.rduePlan}
              {...form.getInputProps("rduePlan")}
            />
            <Select
              label="Zalando Plan"
              data={[
                { label: "Yes", value: "ENABLED" },
                { label: "No", value: "DISABLED" },
              ]}
              error={form.errors.zalandoPlan}
              {...form.getInputProps("zalandoPlan")}
            />
            <Button loading={isCreating} type="submit">
              Create
            </Button>
          </Stack>
        </form>
      </Drawer>
    </Stack>
  )
}
